<template>
  <div>
    <v-container>
      <v-row>
        <v-col cols="10">
          <k-card-header title="FI Configuration" :items="headerItems"></k-card-header>
        </v-col>
        <v-col class="mr-auto" cols="2">
            <v-btn
              x-large
              color="primary"
              ref="saveButtonTop"
              class="pl-16 pr-16"
              @click="saveFiConfigSettings()"
              :loading="saving"
              :disabled="!valid"
            >Save</v-btn>
          </v-col>
      </v-row>
    </v-container>

    <v-container v-if="loading">
      <v-progress-circular indeterminate size="50" />
    </v-container>

    <v-container v-if="!loading && config">
      <v-form ref="form" v-model="valid">
        <!-- Enable platform features -->
        <v-row>
          <v-col cols="12">
            <v-card class="detailCard">
              <v-card-title class="card-title">
                <span class="text-h5">Enabled Platform Features</span>
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-checkbox
                      ref="ascadCheckbox"
                      v-model="config.ascadProcessingEnabled"
                      label="ASCAD Processing"
                    ></v-checkbox>
                  </v-row>
                  <v-row>
                    <v-checkbox
                      class="pa-0 ma-0"
                      ref="rewardsProcessingCheckbox"
                      v-model="config.rewardsProcessingEnabled"
                      label="Rewards Processing"
                      @change="this.rewardsProcessingClicked"
                    ></v-checkbox>
                  </v-row>
                  <v-row>
                    <v-checkbox
                      class="pa-0 ma-0"
                      ref="fidilAccountProcessingCheckbox"
                      v-model="config.fidilAccountProcessingEnabled"
                      label="ASCAD 2.0 Processing"
                      :readonly="config.loanStatusProcessingEnabled"
                    ></v-checkbox>
                  </v-row>
                  <v-row>
                    <v-col cols="3" class="pa-0 ma-0">
                    <v-checkbox
                      class="pa-0 ma-0"
                      ref="loanStatusProcessingCheckbox"
                      v-model="config.loanStatusProcessingEnabled"
                      label="Loan Status Processing"
                    ></v-checkbox>
                    </v-col>
                    <v-col class="picker pa-0 ma-0" cols="3">
                    <span v-if="config.loanStatusProcessingEnabled">
                      <text-box-date-picker
                        class="pa-0 ma-0"
                        ref="firstLoansCoreProcessingDate"
                        v-model="config.firstLoansCoreProcessingDate"
                        label="First date of Loans Status Processing"
                      />
                    </span>
                    </v-col>
                  </v-row>
                  <v-row class="picker">
                    <text-box-date-picker
                      class="pa-0 ma-0"
                      ref="firstDateAccountEvents"
                      v-model="config.earliestReloadDate"
                      label="First date of Account Events"
                    />
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Value Added Fees Settings -->
        <v-row>
          <v-col cols="12">
            <v-card class="detailCard">
              <v-card-title class="card-title">
                <span class="text-h5">Value Added Fees Settings</span>
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="3" class="pa-0 ma-0">
                    <v-checkbox
                      class="pa-0 ma-0"
                      ref="addedFeesProcessingEnabled"
                      v-model="valueAddedFeeSettings.addedFeesProcessingEnabled"
                      label="Value Added Fees Processing"
                      :disabled="!isValidVafaDateRanges()"
                    ></v-checkbox>
                    </v-col>
                    <v-col class="picker pa-0 ma-0" cols="5">
                    <span v-if="valueAddedFeeSettings.addedFeesProcessingEnabled">
                      <text-box-date-picker
                        class="pa-0 ma-0"
                        ref="firstAddedFeesProcessingCoreProcessingDate"
                        v-model="valueAddedFeeSettings.firstAddedFeesProcessingCoreProcessingDate"
                        label="First date of Value Added Fees Processing"
                        :rules="[validateVafaStartDate]"
                      />
                    </span>
                    </v-col>
                    <v-col class="picker pa-0 ma-0" cols="5">
                    <span v-if="valueAddedFeeSettings.addedFeesProcessingEnabled">
                      <text-box-date-picker
                        class="pa-0 ma-0"
                        ref="finalAddedFeesProcessingCoreProcessingDate"
                        v-model="valueAddedFeeSettings.finalAddedFeesProcessingCoreProcessingDate"
                        label="(Optional) Stop date of Value Added Fees Processing"
                        :clearable="true"
                        :rules="[validateVafaEndDate]"
                      />
                    </span>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- K Upgrade section -->
        <v-row>
          <v-col>
            <v-card class="detailCard">
              <v-card-title class="card-title">
                <span class="text-h5">Kasasa Upgrade</span>
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12">
                    <v-checkbox
                      class="pa-0 ma-0"
                      ref="enabledKasasaUpgradeCheckbox"
                      v-model="enabledKasasaUpgrade"
                      :disabled="allUpgrades.length > 0"
                      label="Kasasa Upgrade"
                    ></v-checkbox>
                    </v-col>
                  </v-row>
                  <v-row v-show="enabledKasasaUpgrade">
                    <v-col cols="12">
                      <kasasa-upgrade-create-dialog
                      ref="kugradeCreateNewDialog"
                      :nonKasasaProductCodes="nonKasasaProductCodes"
                      :kasasaProducts="kasasaProducts"
                      @onError="onUpgradeSaveError"
                      @success="onUpgradeSaved"/>
                      <kasasa-upgrade-view
                      ref="kugradeView"
                      v-model="allUpgrades"
                      width="100%"
                      :productMap="productMap"
                      :nonKasasaProductCodes="nonKasasaProductCodes"
                      :kasasaProducts="kasasaProducts"
                      :loading="upgradesLoading || loading"
                      @onError="onUpgradeSaveError"
                      @deleted="onUpgradeDeleted"
                      @success="onUpgradeSaved"/>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Internet Banking File -->
        <v-row>
          <v-col cols="12">
            <v-card class="detailCard">
              <v-card-title class="card-title">
                <span class="text-h5">Internet Banking File</span>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-container>
                  <v-row>
                    <v-col md="6">
                      <v-checkbox
                        class="pa-0 ma-0"
                        ref="ibloginOnlyOnLastProcessingDateCheckbox"
                        v-model="config.requireIbLoginOnlyOnLastProcessingDay"
                      >
                        <template v-slot:label>
                          <div>
                            Only
                            <strong>require</strong> internet banking file on last
                            processing day of the cycle
                          </div>
                        </template>
                      </v-checkbox>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Login file delivery time -->
        <v-scroll-y-transition>
          <v-row
            align="center"
            ref="loginFileDeliveryTimeRow"
            v-if="config.requireIbLoginOnlyOnLastProcessingDay"
          >
            <v-col cols="12">
              <v-card class="detailCard">
                <v-card-title class="card-title">
                  <span class="text-h5">IB Login File Delivery Time</span>
                </v-card-title>
                <v-card-text class="pa-0">
                  <v-container>
                    <v-row>
                      <v-col class="picker">
                        <v-menu
                          class="pa-0 ma-0"
                          ref="ibLoginFileDeliveryTimeMenu"
                          max-width="300px"
                          min-width="300px"
                          offset-y
                          transition="scale-transition"
                          :nudge-right="40"
                          :return-value.sync="config.ibLoginFileDeliveryTime"
                          :close-on-content-click="false"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="config.ibLoginFileDeliveryTime"
                              label="Please select time (CST)"
                              append-icon="fa-clock"
                              :readonly="!config.ibLoginFileDeliveryTime"
                              v-bind="attrs"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-time-picker
                            v-model="config.ibLoginFileDeliveryTime"
                            full-width
                            format="ampm"
                            @click:minute="
                            $refs.ibLoginFileDeliveryTimeMenu.save(
                              config.ibLoginFileDeliveryTime
                            )
                          "
                          ></v-time-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-scroll-y-transition>

        <!-- Expected File Delivery Time -->
        <v-row align="center">
          <v-col cols="12">
            <v-card class="detailCard">
              <v-card-title class="card-title">
                <span class="text-h5">Expected File Delivery Time</span>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-container>
                  <v-row no-gutters>
                    <v-col class="picker">
                      <v-menu
                        class="pa-0 ma-0"
                        ref="coreFileDeliveryTimeMenu"
                        max-width="300px"
                        min-width="300px"
                        offset-y
                        transition="scale-transition"
                        :nudge-right="40"
                        :return-value.sync="config.coreFileDeliveryTime"
                        :close-on-content-click="false"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="config.coreFileDeliveryTime"
                            label="Please Select Time (CST)"
                            append-icon="fa-clock"
                            :readonly="!config.coreFileDeliveryTime"
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-time-picker
                          v-model="config.coreFileDeliveryTime"
                          full-width
                          format="ampm"
                          @click:minute="
                          $refs.coreFileDeliveryTimeMenu.save(
                            config.coreFileDeliveryTime
                          )
                        "
                        ></v-time-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Linked Account Methodology -->
        <v-row>
          <v-col cols="12">
            <v-card class="detailCard">
              <v-card-title class="card-title">
                <span class="text-h5">Linked Account Methodology</span>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-container>
                  <v-row>
                    <v-col>
                      <v-radio-group v-model="config.linkedAccountMethodology">
                        <v-radio
                          value="DIRECT_DEPOSIT_FILE"
                          label="Link Accounts by Account Number"
                          name="linkByAccountNumberRadio"
                        />

                        <v-row no-gutters>
                          <v-col>
                            <v-radio
                              value="PRODUCT_CODE_RELATIONSHIP"
                              label="Link Accounts by Product Code"
                              ref="linkByProductCodeRadio"
                            />
                          </v-col>
                          <v-col>
                            <v-btn
                              id="btn-link-prod-code"
                              v-if="
                              config.linkedAccountMethodology === 'PRODUCT_CODE_RELATIONSHIP'
                            "
                              @click="showLinkingSheet = true"
                              color="primary"
                            >Configure Linking by Product Code</v-btn>
                          </v-col>
                        </v-row>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Posting File Transmission -->
        <v-row>
          <v-col cols="12">
            <v-card class="detailCard">
              <v-card-title class="card-title">
                <span class="text-h5">Posting File Transmission</span>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-container>
                  <v-row>
                    <v-col md="6">
                      <h3>File Transmission Method</h3>
                      <v-radio-group
                        v-model="config.sendFilesDirectToCore"
                        id="transmissionMethodRadioGroup"
                      >
                        <v-radio
                          :value="false"
                          ref="transmissionMethodFit"
                          label="Send files to the FI through FIT"
                        />
                        <v-radio
                          :value="true"
                          ref="transmissionMethodDirect"
                          label="Send Files Direct to Core"
                          @click="sendFilesDirectToCoreClicked"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <h3>Posting Files In Dated Folder</h3>
                      <v-radio-group v-model="config.postingFilesInDatedFolder">
                        <v-radio
                          :value="true"
                          label="Yes"
                          :disabled="config.sendFilesDirectToCore"
                          ref="postingFilesInFolderRadioTrue"
                        />
                        <v-radio :value="false" label="No" ref="postingFilesInFolderRadioFalse" />
                      </v-radio-group>
                    </v-col>

                    <v-col
                      v-if="config.postingFilesInDatedFolder"
                      ref="postingFilesDatedFolderFormat"
                    >
                      <h3>Posting Files Dated Folder Format</h3>
                      <v-radio-group
                        v-model="config.postingFileDateFormat"
                        :rules="[dateFormatRule]"
                        ref="folderFormat"
                        :error="config.postingFilesInDatedFolder && config.postingFileDateFormat == null"
                      >
                        <v-row no-gutters>
                          <v-col>
                            <v-radio value="YYYY_MM_DD" label="yyyy-mm-dd" ref="dateFormat1" />
                          </v-col>
                          <v-col>
                            <v-radio value="YYYY_MM" label="yyyy-mm" ref="dateFormat2" />
                          </v-col>
                        </v-row>
                        <v-row no-gutters class="gapped">
                          <v-col>
                            <v-radio value="YYYYMMDD" label="yyyymmdd" ref="dateFormat3" />
                          </v-col>
                          <v-col>
                            <v-radio value="YYYYMM" label="yyyymm" ref="dateFormat4" />
                          </v-col>
                        </v-row>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                  <v-row class="py-0">
                    <v-col class="py-0">
                      <h3>Automated End of Cycle</h3>
                      <v-checkbox hide-details dense v-model="config.endOfCyclePostingFilesAutomated" label="End Of Cycle Posting Files" ref="endOfCyclePostingFilesAutomatedCheck"/>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col class="py-0">
                      <v-checkbox hide-details dense v-model="config.secondDaySweepFilesAutomated" label="Second Day Sweep Files" ref="secondDaySweepAutomatedCheck"/>
                    </v-col>
                  </v-row>
                  <v-expand-transition>
                    <v-row class="ml-5" v-if="config.endOfCyclePostingFilesAutomated || config.secondDaySweepFilesAutomated">
                      <v-col cols="3" class="pb-0">
                        <strong>File Delivery Time</strong>
                          <v-menu
                            ref="automatedFileDeliveryTimeMenu"
                            max-width="300px"
                            min-width="300px"
                            offset-y
                            transition="scale-transition"
                            dense
                            :nudge-right="40"
                            :return-value.sync="config.automatedFileDeliveryTime"
                            :close-on-content-click="false"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="config.automatedFileDeliveryTime"
                                label="Please Select Time (CST)"
                                append-icon="fa-clock"
                                :readonly="!config.automatedFileDeliveryTime"
                                v-bind="attrs"
                                v-on="on"
                                class="pt-1"
                                :error-messages="automatedFileDeliveryTimeError"
                              ></v-text-field>
                            </template>
                            <v-time-picker
                              v-model="config.automatedFileDeliveryTime"
                              full-width
                              format="ampm"
                              @click:minute="
                              $refs.automatedFileDeliveryTimeMenu.save(
                                config.automatedFileDeliveryTime
                              )
                            "
                            ></v-time-picker>
                          </v-menu>
                      </v-col>
                    </v-row>
                  </v-expand-transition>
                  <v-row>
                    <v-col class="pt-0 pb-4">
                      <v-checkbox dense hide-details ref="automatedEOCEmailsCheck" v-model="config.endOfCycleEmailsAutomated" label="End Of Cycle Emails"/>
                    </v-col>
                  </v-row>
                  <v-expand-transition>
                    <v-row v-if="config.endOfCycleEmailsAutomated" class="ml-5">
                      <v-container>
                        <v-row>
                          <v-col cols="3" class="py-0">
                            <strong>Email Delivery Time</strong>
                            <v-menu
                                ref="endOfCycleEmailDeliveryTimeMenu"
                                max-width="300px"
                                min-width="300px"
                                offset-y
                                transition="scale-transition"
                                dense
                                :nudge-right="40"
                                :return-value.sync="config.endOfCycleEmailDeliveryTime"
                                :close-on-content-click="false"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-text-field
                                    v-model="config.endOfCycleEmailDeliveryTime"
                                    label="Please Select Time (CST)"
                                    append-icon="fa-clock"
                                    :readonly="!config.endOfCycleEmailDeliveryTime"
                                    v-bind="attrs"
                                    v-on="on"
                                    :error-messages="automatedEmailDeliveryTimeError"
                                  ></v-text-field>
                                </template>
                                <v-time-picker
                                  v-model="config.endOfCycleEmailDeliveryTime"
                                  full-width
                                  format="ampm"
                                  @click:minute="
                                  $refs.endOfCycleEmailDeliveryTimeMenu.save(
                                    config.endOfCycleEmailDeliveryTime
                                  )
                                "
                                ></v-time-picker>
                              </v-menu>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col>
                            <h3>Email Delivery Day</h3>
                              <v-radio-group v-model="config.deliveryDayOffset" ref="deliveryDayOffsetRadio" hide-details dense>
                                <v-radio :value="0" label="Drop Day" ref="dropDayRadio"/>
                                <v-radio :value="1" label="1 Processing Day After" ref="oneDayAfterRadio"/>
                                <v-radio :value="2" label="2 Processing Days After" ref="twoDaysAfterRadio"/>
                              </v-radio-group>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-row>
                  </v-expand-transition>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!--Fl Report -->
        <v-row>
          <v-col cols="12">
            <v-card class="border-shadow">
              <v-card-title class="card-title">
                <span class="text-h5">FI Report Format</span>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-container>
                  <v-row>
                    <v-col class="picker">
                        <h3>Select Format for Files sent to FI</h3>
                        <span>
                          <v-select
                            outlined
                            v-model="config.fiReportFileFormat"
                            :items="fileFormats"
                            label="File formats"
                            dense
                            class="mt-4"
                            ref="fiReportFormat"
                          ></v-select>
                        </span>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Save button 2 -->
        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="auto" class="mr-auto">
            <v-btn
              x-large
              color="primary"
              ref="saveButton"
              class="pl-16 pr-16"
              @click="saveFiConfigSettings()"
              :loading="saving"
              :disabled="!valid"
            >Save</v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>

    <linking-sheet v-model="showLinkingSheet" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Inject, Watch,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import TextBoxDatePicker from '@/components/TextBoxDatePicker.vue';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';
import { KCardHeader, KCardHeaderBtn } from '@kasasa/fbase-components';
import KasasaUpgradeView from '@/views/KasasaUpgradeView.vue';
import LinkingSheet from '@/components/ficonfigurationsettings/LinkingSheet.vue';
import {
  FiConfiguration,
  FiConfigurationResource,
} from '@/api/ficonfiguration';
import {
  DateTool, DisplayAccountProductCode, DisplayProduct, KasasaAccountUpgradeUtil,
} from '@/util';
import KasasaUpgradeCreateDialog from '@/components/KasasaUpgradeCreateDialog.vue';
import { KasasaUpgradeResource, KasasaUpgrade } from '@/api/kasasaupgrade';
import { ProductMapping, ProductMappingResource } from '@/api/productmapping';
import { ValueAddedFeesResource } from '@/api/vafa/ValueAddedFeesResource';
import { ValueAddedFeeSettings } from '@/api/vafa/ValueAddedFeeSettings';

@Component({
  computed: mapState(['fiId', 'configVersion']),
  components: {
    KCardHeader,
    KCardHeaderBtn,
    TextBoxDatePicker,
    LinkingSheet,
    KasasaUpgradeView,
    KasasaUpgradeCreateDialog,
  },
})
export default class FiConfigurationSettings extends Vue {
  headerItems: any = [
    {
      text: 'Rewards Configuration',
      disabled: false,
      link: false,
    },
    {
      text: 'FI Configuration',
      disabled: true,
    },
  ];

  @Inject('FiConfigurationResource')
  fiConfigurationResource!: FiConfigurationResource;

  @Inject('KasasaUpgradeResource')
  kasasaUpgradeResource!: KasasaUpgradeResource;

  @Inject('ProductMappingResource')
  productMappingResource!: ProductMappingResource;

  @Inject('ValueAddedFeesResource')
  valueAddedFeesResource!: ValueAddedFeesResource;

  fiId!: number;

  config: FiConfiguration | null = null;

  loading: boolean = false;

  upgradesLoading: boolean = false;

  saving: boolean = false;

  valid: boolean = false;

  fileFormats = ['XLSX', 'CSV', 'TXT'];

  showLinkingSheet: boolean = false;

  enabledKasasaUpgrade: boolean = false;

  automatedFileDeliveryTimeError:string | null = null;

  automatedEmailDeliveryTimeError: string | null = null;

  allUpgrades: KasasaUpgrade[] = [];

  productMap: Map<number, ProductMapping> = new Map();

  nonKasasaProductCodes: DisplayAccountProductCode[] = [];

  kasasaProducts: DisplayProduct[] = [];

  valueAddedFeeSettings: ValueAddedFeeSettings | null = null;

  static AUTOMATED_DELIVERY_TIME_TOO_LATE:string = 'Delivery time must be earlier than 11pm';

  static DELIVERY_TIME_IS_REQUIRED:string = 'Delivery time is required';

  mounted() {
    this.loadConfig();
  }

  @Watch('fiId')
  loadConfig() {
    this.config = null;
    this.loading = true;
    this.upgradesLoading = true;
    this.valueAddedFeeSettings = null;

    const allPromises:Promise<any>[] = [];
    allPromises.push(this.fiConfigurationResource.getFiConfiguration(this.fiId));
    allPromises.push(this.productMappingResource.get(this.fiId));
    allPromises.push(this.kasasaUpgradeResource.getAll(this.fiId));
    allPromises.push(this.valueAddedFeesResource.getCurrentSettings(this.fiId));

    Promise.all(allPromises).then((results:any[]) => {
      const configurations: FiConfiguration = results[0];
      this.resetConfigurations(configurations);

      const productMappings: ProductMapping[] = results[1];
      KasasaAccountUpgradeUtil.buildProductMap(productMappings, this.productMap);

      this.allUpgrades = results[2];
      if (this.allUpgrades && this.allUpgrades.length > 0) {
        this.enabledKasasaUpgrade = true;
      } else {
        this.enabledKasasaUpgrade = false;
      }

      const valueAddedFeeSettings : ValueAddedFeeSettings = results[3];
      this.resetValueAddedFees(valueAddedFeeSettings);

      this.kasasaProducts = [];
      this.nonKasasaProductCodes = [];
      KasasaAccountUpgradeUtil.buildProductListsForSelect(productMappings, this.kasasaProducts, this.nonKasasaProductCodes);
    }).catch((error) => {
      this.showError(error);
    }).finally(() => {
      this.loading = false;
      this.upgradesLoading = false;
    });
  }

  reloadKasasaUpgrades() {
    this.upgradesLoading = true;
    this.kasasaUpgradeResource.getAll(this.fiId).then((upgrades: KasasaUpgrade[]) => {
      this.allUpgrades = upgrades;
      if (this.allUpgrades && this.allUpgrades.length > 0) {
        this.enabledKasasaUpgrade = true;
      } else {
        this.enabledKasasaUpgrade = false;
      }
    })
      .catch((error:any) => {
        this.showError(error);
      })
      .finally(() => {
        this.upgradesLoading = false;
      });
  }

  static simplifyTime(time: string | null): string | null {
    if (time !== null) {
      return time.substring(0, 5);
    }
    return null;
  }

  resetConfigurations(configurations: FiConfiguration) {
    this.config = { ...configurations };

    // Strip everything but hours and seconds from times
    this.config.coreFileDeliveryTime = FiConfigurationSettings.simplifyTime(this.config.coreFileDeliveryTime);
    this.config.ibLoginFileDeliveryTime = FiConfigurationSettings.simplifyTime(this.config.ibLoginFileDeliveryTime);
    this.config.automatedFileDeliveryTime = FiConfigurationSettings.simplifyTime(this.config.automatedFileDeliveryTime);
    this.config.endOfCycleEmailDeliveryTime = FiConfigurationSettings.simplifyTime(this.config.endOfCycleEmailDeliveryTime);

    if (!this.config.firstLoansCoreProcessingDate) {
      this.config.firstLoansCoreProcessingDate = DateTool.todayBackendDate();
    }
  }

  resetValueAddedFees(settings: ValueAddedFeeSettings) {
    this.valueAddedFeeSettings = { ...settings };

    if (!this.valueAddedFeeSettings.firstAddedFeesProcessingCoreProcessingDate) {
      this.valueAddedFeeSettings.firstAddedFeesProcessingCoreProcessingDate = DateTool.todayBackendDate();
    }
  }

  @Watch('valueAddedFeeSettings.addedFeesProcessingEnabled')
  @Watch('valueAddedFeeSettings.firstAddedFeesProcessingCoreProcessingDate')
  @Watch('valueAddedFeeSettings.finalAddedFeesProcessingCoreProcessingDate')
  onValueAddedFeesChanged() {
    if (this.$refs.form) (this.$refs.form as any).validate();
  }

  dateFormatRule = (value: string | null): boolean | string => {
    if (value == null) {
      return 'Posting File Date Folder Format is required.';
    }
    return true;
  }

  showError(error: any) {
    if (error.response && error.response.data && error.response.data.entity) {
      this.setError(error.response.data.entity);
    } else {
      this.setError('An unexpected error occurred.');
    }
  }

  setError(message: string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  rewardsProcessingClicked() {
    if (this.config && this.config.rewardsProcessingEnabled) {
      this.config.ascadProcessingEnabled = true;
    }
  }

  @Watch('config.loanStatusProcessingEnabled')
  onLoanStatusProcessingChange() {
    const config: FiConfiguration = this.config!;
    if (config && config.loanStatusProcessingEnabled) {
      config.fidilAccountProcessingEnabled = true;
    }
  }

  sendFilesDirectToCoreClicked() {
    if (this.config && this.config.sendFilesDirectToCore) {
      this.config.postingFilesInDatedFolder = false;
      (this.$refs.form as any).validate();
    }
  }

  @Watch('config.endOfCyclePostingFilesAutomated')
  @Watch('config.secondDaySweepFilesAutomated')
  @Watch('config.endOfCycleEmailsAutomated')
  @Watch('config.automatedFileDeliveryTime')
  @Watch('config.endOfCycleEmailDeliveryTime')
  validateAutomatedDeliveryTimes() {
    if (this.config?.endOfCyclePostingFilesAutomated === true || this.config?.secondDaySweepFilesAutomated === true) {
      if (FiConfigurationSettings.timeIsTooLateForAutomatedEOC(this.config?.automatedFileDeliveryTime)) {
        this.automatedFileDeliveryTimeError = FiConfigurationSettings.AUTOMATED_DELIVERY_TIME_TOO_LATE;
      } else if (this.config?.automatedFileDeliveryTime === null || this.config?.automatedFileDeliveryTime.length === 0) {
        this.automatedFileDeliveryTimeError = FiConfigurationSettings.DELIVERY_TIME_IS_REQUIRED;
      } else {
        this.automatedFileDeliveryTimeError = null;
      }
    }

    if (this.config?.endOfCycleEmailsAutomated === true) {
      if (FiConfigurationSettings.timeIsTooLateForAutomatedEOC(this.config?.endOfCycleEmailDeliveryTime)) {
        this.automatedEmailDeliveryTimeError = FiConfigurationSettings.AUTOMATED_DELIVERY_TIME_TOO_LATE;
      } else if (this.config.endOfCycleEmailDeliveryTime === null || this.config.endOfCycleEmailDeliveryTime.length === 0) {
        this.automatedEmailDeliveryTimeError = FiConfigurationSettings.DELIVERY_TIME_IS_REQUIRED;
      } else {
        this.automatedEmailDeliveryTimeError = null;
      }
    }
  }

  static timeIsTooLateForAutomatedEOC(time:string | null | undefined) : boolean {
    if (time) {
      const elements:string[] = time.split(':');
      if (elements.length > 0) {
        const hours:string = elements[0];
        if (parseInt(hours, 10) === 23) {
          return true;
        }
      }
    }
    return false;
  }

  validateVafaStartDate(value: any) : boolean | string {
    const firstDateIsNull:boolean = value === null || value.length === 0;
    const firstDate:Date|null = firstDateIsNull ? null : new Date(value);
    const finalDateString:string|null = this.valueAddedFeeSettings?.finalAddedFeesProcessingCoreProcessingDate || null;
    const finalDate:Date|null = finalDateString === null || finalDateString.length === 0 ? null : DateTool.parseDate(finalDateString);
    const vafaEnabled:boolean = this.valueAddedFeeSettings?.addedFeesProcessingEnabled === true;

    if (!vafaEnabled) {
      return true;
    }

    if (firstDateIsNull) {
      return 'First processing date of value added fees is required';
    }

    if (finalDate !== null && finalDate.getTime() < firstDate!.getTime()) {
      return 'First processing date must be on or before final processing date if final processing date is set';
    }

    return true;
  }

  validateVafaEndDate(value: any) : boolean | string {
    const finalDateIsNull:boolean = value === null || value.length === 0;
    const finalDate:Date|null = finalDateIsNull ? null : new Date(value);
    const firstDateString:string|null = this.valueAddedFeeSettings?.firstAddedFeesProcessingCoreProcessingDate || null;
    const firstDate:Date|null = firstDateString === null || firstDateString.length === 0 ? null : DateTool.parseDate(firstDateString);
    const vafaEnabled:boolean = this.valueAddedFeeSettings?.addedFeesProcessingEnabled === true;

    if (!vafaEnabled) {
      return true;
    }

    if (firstDate !== null && finalDate !== null && finalDate.getTime() < firstDate!.getTime()) {
      return 'Final processing date must be on or after first processing date if final processing date is set';
    }

    return true;
  }

  isValidVafaDateRanges(): boolean {
    const vafaEnabled:boolean = this.valueAddedFeeSettings?.addedFeesProcessingEnabled || false;
    if (!vafaEnabled) {
      return true;
    }
    const firstDateString:string|null = this.valueAddedFeeSettings?.firstAddedFeesProcessingCoreProcessingDate || null;
    const firstDate:Date|null = firstDateString === null || firstDateString.length === 0 ? null : DateTool.parseDate(firstDateString);
    const finalDateString:string|null = this.valueAddedFeeSettings?.finalAddedFeesProcessingCoreProcessingDate || null;
    const finalDate:Date|null = finalDateString === null || finalDateString.length === 0 ? null : DateTool.parseDate(finalDateString);

    if (firstDate === null) {
      return false;
    }

    if (finalDate === null) {
      return true;
    }

    return finalDate.getTime() >= firstDate.getTime();
  }

  saveFiConfigSettings() {
    this.saving = true;

    const allPromises:Promise<any>[] = [];
    allPromises.push(this.fiConfigurationResource.updateFiConfiguration(this.fiId, this.config!));
    allPromises.push(this.valueAddedFeesResource.save(this.fiId, this.valueAddedFeeSettings!));

    Promise.all(allPromises).then((results:any[]) => {
      const configurations: FiConfiguration = results[0];
      const valueAddSettings: ValueAddedFeeSettings = results[1];
      this.resetConfigurations(configurations);
      this.resetValueAddedFees(valueAddSettings);
      const al = new Alert('FI Configuration saved successfully.', NoticeClass.SUCCESS);
      al.setTimeout(5000);
      this.$store.dispatch('notices/add', al);
    }).catch((error) => {
      this.showError(error);
    }).finally(() => {
      this.saving = false;
    });
  }

  onUpgradeSaved(savedUpgrade:KasasaUpgrade) {
    const al = new Alert('FI Kasasa Upgrade Configuration saved successfully.', NoticeClass.SUCCESS);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);

    this.allUpgrades = [];
    this.reloadKasasaUpgrades();
  }

  onUpgradeDeleted(savedUpgrade:KasasaUpgrade) {
    const al = new Alert('FI Kasasa Upgrade Configuration deleted successfully.', NoticeClass.SUCCESS);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);

    this.allUpgrades = [];
    this.reloadKasasaUpgrades();
  }

  onUpgradeSaveError(error:any) {
    this.showError(error);
  }
}
</script>

<style lang="scss" scoped>
.picker {
  width: 100%;
  max-width: 300px !important;
}
</style>
